import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Extraction, Attachment, api } from '../api/client';
import ComposeModal from './ComposeModal';

interface Props {
  extraction: Extraction;
}

// ─── Status config (Fluent palette) ──────────────────────────────────────────
const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
  COMPLETED:    { bg: '#DFF6DD', text: '#107C10', dot: '#107C10' },
  RECEIVED:     { bg: '#DFF6DD', text: '#107C10', dot: '#107C10' },
  TASK_CREATED: { bg: '#EBF3FB', text: '#0078D4', dot: '#0078D4' },
  FAILED:       { bg: '#FDE7E9', text: '#A4262C', dot: '#A4262C' },
  PROCESSING:   { bg: '#FFF4CE', text: '#8A8000', dot: '#C8A600' },
  PENDING:      { bg: '#F3F2F1', text: '#605E5C', dot: '#8A8886' },
};

function formatSize(bytes: number): string {
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

type FileType = 'pdf' | 'word' | 'excel' | 'image' | 'zip' | 'other';

function getFileType(contentType: string): FileType {
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.includes('word') || contentType.includes('msword')) return 'word';
  if (contentType.includes('excel') || contentType.includes('spreadsheet') || contentType.includes('csv')) return 'excel';
  if (contentType.includes('zip') || contentType.includes('compressed') || contentType.includes('tar') || contentType.includes('rar')) return 'zip';
  return 'other';
}

const fileTypeStyle: Record<FileType, { bg: string; color: string }> = {
  pdf:   { bg: '#FFF5F5', color: '#E53E3E' },
  word:  { bg: '#EBF8FF', color: '#3182CE' },
  excel: { bg: '#F0FFF4', color: '#38A169' },
  image: { bg: '#FAF5FF', color: '#805AD5' },
  zip:   { bg: '#FFFAF0', color: '#DD6B20' },
  other: { bg: '#F7FAFC', color: '#718096' },
};

const FileTypeIcon: React.FC<{ contentType: string; name: string }> = ({ contentType, name }) => {
  const type = getFileType(contentType);
  const { bg, color } = fileTypeStyle[type];
  const ext = name.split('.').pop()?.toUpperCase().slice(0, 4) || 'FILE';
  return (
    <div style={{
      width: 42, height: 42, flexShrink: 0,
      borderRadius: '8px', background: bg,
      border: `1px solid ${color}33`,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '1px',
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
      </svg>
      <span style={{ fontSize: '7px', fontWeight: 800, color, letterSpacing: '.04em', lineHeight: 1 }}>{ext}</span>
    </div>
  );
};

const AttachmentCard: React.FC<{ att: Attachment; extractionId: string }> = ({ att, extractionId }) => {
  const [dlState, setDlState] = useState<'idle' | 'loading' | 'error'>('idle');

  const handleDownload = async () => {
    setDlState('loading');
    try {
      await api.downloadAttachment(extractionId, att.id, att.name);
      setDlState('idle');
    } catch {
      setDlState('error');
      setTimeout(() => setDlState('idle'), 3000);
    }
  };

  const btnBg   = dlState === 'error' ? '#FFF5F5' : 'var(--accent-light)';
  const btnColor = dlState === 'error' ? '#E53E3E' : 'var(--accent)';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      padding: '10px 12px',
      background: '#fff',
      border: '1px solid var(--border)',
      borderRadius: '8px',
      minWidth: '190px', maxWidth: '270px',
      flex: '1 0 auto',
      transition: 'box-shadow .15s',
    }}
      onMouseEnter={(ev) => { ev.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'; }}
      onMouseLeave={(ev) => { ev.currentTarget.style.boxShadow = 'none'; }}
    >
      <FileTypeIcon contentType={att.contentType} name={att.name} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 600, fontSize: '12.5px', color: 'var(--text)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={att.name}>
          {att.name}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>
          {formatSize(att.size)}
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={dlState === 'loading'}
        title={dlState === 'error' ? 'Download failed — click to retry' : `Download ${att.name}`}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 30, height: 30, flexShrink: 0,
          border: 'none', borderRadius: '6px',
          cursor: dlState === 'loading' ? 'default' : 'pointer',
          background: btnBg, color: btnColor,
          transition: 'background .12s',
        }}
        onMouseEnter={(ev) => { if (dlState === 'idle') ev.currentTarget.style.background = '#dbeafe'; }}
        onMouseLeave={(ev) => { if (dlState === 'idle') ev.currentTarget.style.background = btnBg; }}
      >
        {dlState === 'loading' ? (
          <span style={{
            display: 'inline-block', width: 12, height: 12,
            border: '2px solid #e2e8f0', borderTopColor: 'var(--accent)',
            borderRadius: '50%', animation: 'spin 0.7s linear infinite',
          }} />
        ) : dlState === 'error' ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        ) : (
          <IconDownload />
        )}
      </button>
    </div>
  );
};

/**
 * Wrap plain text in a minimal HTML document with nice typography
 * and safe link detection. Text is HTML-escaped before link processing.
 */
function textToHtmlDoc(text: string, zoom: number): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const linked = escaped.replace(
    /https?:\/\/[^\s<>"'&]+/g,
    (url) => `<a href="${url}" target="_blank" rel="noreferrer" style="color:#2563eb;word-break:break-all">${url}</a>`,
  );
  const paragraphs = linked
    .split(/\n{2,}/)
    .map((block) => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('\n');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>
  * { box-sizing:border-box; margin:0; padding:0; }
  html { font-size:${zoom}%; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px; line-height: 1.75; color: #1e293b;
    padding: 20px 24px; background:#fff;
    -webkit-font-smoothing: antialiased;
  }
  p { margin-bottom: 1em; }
  p:last-child { margin-bottom: 0; }
  a { color:#2563eb; }
</style></head><body>${paragraphs}</body></html>`;
}

/**
 * Inject a base font-size style into an existing HTML email body.
 * Inserts into <head> if present, prepends otherwise.
 */
function injectZoom(html: string, zoom: number): string {
  const style = `<style>html{font-size:${zoom}%}</style>`;
  if (/<head[\s>]/i.test(html)) {
    return html.replace(/<head([\s>])/i, `<head$1${style}`);
  }
  return style + html;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconDownload = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" x2="12" y1="15" y2="3"/>
  </svg>
);
const IconZoomIn  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" x2="16.65" y1="21" y2="16.65"/><line x1="11" x2="11" y1="8" y2="14"/><line x1="8" x2="14" y1="11" y2="11"/></svg>;
const IconZoomOut = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" x2="16.65" y1="21" y2="16.65"/><line x1="8" x2="14" y1="11" y2="11"/></svg>;
const IconExpand  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" x2="14" y1="3" y2="10"/><line x1="3" x2="10" y1="21" y2="14"/></svg>;
const IconCollapse = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="10" x2="3" y1="14" y2="21"/><line x1="21" x2="14" y1="3" y2="10"/></svg>;
const IconPrint   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>;
const IconTask    = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/><polyline points="9 12 11 14 15 10"/></svg>;

// ─── Shared sub-components ────────────────────────────────────────────────────
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: '10px' }}>
    {children}
  </div>
);


const Divider: React.FC = () => (
  <div style={{ width: 1, height: 16, background: 'var(--border)', flexShrink: 0 }} />
);

const ToolbarBtn: React.FC<{
  onClick: () => void;
  title: string;
  active?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ onClick, title, active, disabled, children }) => (
  <button
    title={title}
    onClick={onClick}
    disabled={disabled}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      padding: '4px 9px', border: 'none', borderRadius: '5px', cursor: disabled ? 'default' : 'pointer',
      fontSize: '12px', fontWeight: 600,
      background: active ? '#2563eb' : 'transparent',
      color: active ? '#fff' : disabled ? 'var(--text-3)' : 'var(--text-2)',
      transition: 'background .12s, color .12s',
      opacity: disabled ? 0.45 : 1,
    }}
    onMouseEnter={(ev) => { if (!disabled && !active) ev.currentTarget.style.background = '#f1f5f9'; }}
    onMouseLeave={(ev) => { if (!disabled && !active) ev.currentTarget.style.background = 'transparent'; }}
  >
    {children}
  </button>
);

// ─── Body viewer ──────────────────────────────────────────────────────────────
const BodyViewer: React.FC<{
  bodyHtml?: string;
  bodyText?: string;
}> = ({ bodyHtml, bodyText }) => {
  const [mode, setMode]           = useState<'html' | 'text'>(bodyHtml ? 'html' : 'text');
  const [zoom, setZoom]           = useState(100);
  const [expanded, setExpanded]   = useState(false);
  const [iframeH, setIframeH]     = useState(480);
  const [loading, setLoading]     = useState(true);
  const iframeRef                 = useRef<HTMLIFrameElement>(null);
  const MAX_H                     = expanded ? 9999 : 680;

  const zoomStep = 10;
  const zoomMin  = 60;
  const zoomMax  = 150;

  // Recompute iframe height on load
  const handleLoad = useCallback(() => {
    setLoading(false);
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (doc?.documentElement) {
        const h = doc.documentElement.scrollHeight || doc.body?.scrollHeight || 480;
        setIframeH(Math.max(h + 8, 200));
      }
    } catch { /* sandboxed */ }
  }, []);

  // Re-trigger load indicator when mode or zoom changes
  useEffect(() => { setLoading(true); }, [mode, zoom]);

  const srcDoc =
    mode === 'html' && bodyHtml
      ? injectZoom(bodyHtml, zoom)
      : textToHtmlDoc(bodyText ?? '', zoom);

  const clampedH = Math.min(iframeH, MAX_H);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>

      {/* ── Toolbar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '4px',
        padding: '7px 10px',
        background: '#f8fafc',
        borderBottom: '1px solid var(--border)',
        flexWrap: 'wrap',
      }}>
        {/* Mode toggle — only if both exist */}
        {bodyHtml && bodyText && (
          <>
            <ToolbarBtn onClick={() => setMode('html')} title="Rendered HTML" active={mode === 'html'}>HTML</ToolbarBtn>
            <ToolbarBtn onClick={() => setMode('text')} title="Plain text"    active={mode === 'text'}>Text</ToolbarBtn>
            <Divider />
          </>
        )}

        {/* Zoom */}
        <ToolbarBtn onClick={() => setZoom((z) => Math.max(zoomMin, z - zoomStep))} title="Zoom out" disabled={zoom <= zoomMin}>
          <IconZoomOut />
        </ToolbarBtn>
        <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-3)', minWidth: 36, textAlign: 'center' }}>
          {zoom}%
        </span>
        <ToolbarBtn onClick={() => setZoom((z) => Math.min(zoomMax, z + zoomStep))} title="Zoom in" disabled={zoom >= zoomMax}>
          <IconZoomIn />
        </ToolbarBtn>
        {zoom !== 100 && (
          <ToolbarBtn onClick={() => setZoom(100)} title="Reset zoom">Reset</ToolbarBtn>
        )}

        <Divider />

        {/* Expand */}
        <ToolbarBtn onClick={() => setExpanded((x) => !x)} title={expanded ? 'Collapse' : 'Expand'}>
          {expanded ? <><IconCollapse /> Collapse</> : <><IconExpand /> Expand</>}
        </ToolbarBtn>

        {/* Print */}
        <ToolbarBtn
          title="Print email"
          onClick={() => {
            const iframe = iframeRef.current;
            if (iframe?.contentWindow) {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
            }
          }}
        >
          <IconPrint /> Print
        </ToolbarBtn>

        {/* Loading indicator */}
        {loading && (
          <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span style={{
              display: 'inline-block', width: 11, height: 11,
              border: '2px solid #e2e8f0', borderTopColor: '#2563eb',
              borderRadius: '50%', animation: 'spin 0.7s linear infinite',
            }} />
            Loading…
          </span>
        )}
      </div>

      {/* ── Content ── */}
      <div style={{ position: 'relative', overflow: 'hidden', background: '#fff' }}>
        <iframe
          key={`${mode}-${zoom}`}         /* remount when mode or zoom changes */
          ref={iframeRef}
          srcDoc={srcDoc}
          onLoad={handleLoad}
          style={{
            width: '100%',
            height: `${clampedH}px`,
            border: 'none',
            display: 'block',
            transition: 'height .3s ease',
          }}
          sandbox="allow-same-origin allow-popups"
          title="Email body"
        />
        {/* Fade-out hint when content is taller than the capped height */}
        {!expanded && iframeH > MAX_H && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: 60,
            background: 'linear-gradient(transparent, rgba(255,255,255,.95))',
            pointerEvents: 'none',
          }} />
        )}
      </div>

      {/* ── Show more ── */}
      {!expanded && iframeH > MAX_H && (
        <div style={{
          textAlign: 'center', padding: '10px',
          borderTop: '1px solid var(--border)', background: '#f8fafc',
        }}>
          <button
            className="btn-ghost"
            onClick={() => setExpanded(true)}
            style={{ fontSize: '12px', padding: '4px 14px' }}
          >
            Show full email ↓
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Tasks panel ─────────────────────────────────────────────────────────────

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const priorityConfig: Record<string, { bg: string; color: string }> = {
  High:     { bg: '#FFF5F5', color: '#E53E3E' },
  Medium:   { bg: '#FFFAF0', color: '#DD6B20' },
  Low:      { bg: '#F0FFF4', color: '#38A169' },
  Critical: { bg: '#FFF5F5', color: '#C53030' },
};

const TaskCard: React.FC<{ ticketId: number }> = ({ ticketId }) => {
  const [data, setData]     = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.getAmsActivities(ticketId)
      .then((res) => { setData(res); })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load task'))
      .finally(() => setLoading(false));
  }, [ticketId]);

  const task = data?.data ?? data?.result ?? data ?? null;

  const priority: string = task?.priority ?? task?.default_priority ?? '';
  const pc = priorityConfig[priority] ?? { bg: '#F7FAFC', color: '#718096' };

  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden',
      background: '#fff', transition: 'box-shadow .15s',
    }}
      onMouseEnter={(ev) => { ev.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.08)'; }}
      onMouseLeave={(ev) => { ev.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Card header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 14px', borderBottom: '1px solid var(--border)',
        background: '#f8fafc',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round">
            <rect x="9" y="2" width="6" height="4" rx="1"/>
            <path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/>
            <polyline points="9 12 11 14 15 10"/>
          </svg>
          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)' }}>
            Task #{ticketId}
          </span>
        </div>
        {!loading && !error && priority && (
          <span style={{ fontSize: '10px', fontWeight: 700, background: pc.bg, color: pc.color, borderRadius: '4px', padding: '2px 7px' }}>
            {priority}
          </span>
        )}
      </div>

      {/* Card body */}
      <div style={{ padding: '12px 14px' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[70, 50, 85, 55].map((w, i) => (
              <div key={i} className="skeleton" style={{ height: 11, width: `${w}%` }} />
            ))}
          </div>
        ) : error ? (
          <div style={{ fontSize: '12px', color: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            {error}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
            {[
              { label: 'Status',      value: task?.status ?? task?.status_name ?? '—' },
              { label: 'Queue',       value: task?.queue_name ?? task?.queue ?? '—' },
              { label: 'Assigned To', value: task?.assigned_to ?? task?.handler_name ?? task?.assigned_to_handler ?? '—' },
              { label: 'Due Date',    value: formatDate(task?.due_date) },
              { label: 'Flow',        value: task?.flow_name ?? task?.flow ?? '—' },
              { label: 'Created',     value: formatDate(task?.created_at ?? task?.createdAt) },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '2px' }}>
                  {label}
                </div>
                <div style={{ fontSize: '12.5px', color: 'var(--text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(value)}>
                  {String(value)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TasksPanel: React.FC<{ tickets: number[] }> = ({ tickets }) => (
  <div className="card" style={{ padding: '14px 20px', marginBottom: '2px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round">
        <rect x="9" y="2" width="6" height="4" rx="1"/>
        <path d="M9 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2h-2"/>
        <polyline points="9 12 11 14 15 10"/>
      </svg>
      <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-2)', letterSpacing: '.04em', textTransform: 'uppercase' }}>
        Tasks
      </span>
      <span style={{ background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: '10px', padding: '0 7px', fontSize: '11px', fontWeight: 700, lineHeight: '18px' }}>
        {tickets.length}
      </span>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {tickets.map((id) => <TaskCard key={id} ticketId={id} />)}
    </div>
  </div>
);

// ─── Avatar helper ────────────────────────────────────────────────────────────
function senderInitials(name: string): string {
  return name.split(' ').map((w) => w[0] ?? '').slice(0, 2).join('').toUpperCase() || '?';
}
function senderColor(str: string): string {
  const colors = ['#0078D4','#107C10','#5C2D91','#8764B8','#C43E1C','#038387','#004E8C','#69797E'];
  let hash = 0;
  for (const c of str) hash = c.charCodeAt(0) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

// ─── Main component ───────────────────────────────────────────────────────────
const EmailDetail: React.FC<Props> = ({ extraction: e }) => {
  const realAttachments = (e.attachments ?? []).filter((a) => !a.isInline);
  const sc = statusConfig[e.status] ?? statusConfig.PENDING;

  const [taskState, setTaskState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [taskError, setTaskError] = useState('');
  const [emlState, setEmlState]   = useState<'idle' | 'loading' | 'error'>('idle');
  const [composeMode, setComposeMode] = useState<'compose' | 'reply' | 'reply-all' | 'forward' | null>(null);

  const handleCreateTask = async () => {
    setTaskState('loading');
    setTaskError('');
    try {
      await api.createTask(e.id);
      setTaskState('done');
    } catch (err) {
      setTaskError(err instanceof Error ? err.message : 'Failed to create task');
      setTaskState('error');
    }
  };

  const handleDownloadEml = async () => {
    setEmlState('loading');
    try {
      await api.downloadEml(e.id, e.subject);
      setEmlState('idle');
    } catch {
      setEmlState('error');
      setTimeout(() => setEmlState('idle'), 3000);
    }
  };

  const avatarBg = senderColor(e.sender || e.senderEmail);

  const handleCompose = (mode: 'compose' | 'reply' | 'reply-all' | 'forward') => {
    setComposeMode(mode);
  };

  return (
    <div style={{ marginBottom: '16px' }}>

      {/* ── Outlook-style message header ── */}
      <div className="card" style={{ padding: '20px 24px 0', marginBottom: '2px' }}>

        {/* Subject */}
        <h2 style={{
          fontSize: '18px', fontWeight: 600,
          color: 'var(--text)', lineHeight: 1.35,
          marginBottom: '16px',
          letterSpacing: '-0.01em',
        }}>
          {e.subject || '(No Subject)'}
        </h2>

        {/* Sender row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
          {/* Avatar */}
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: avatarBg, color: '#fff', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '14px', fontWeight: 700, letterSpacing: '.03em',
          }}>
            {senderInitials(e.sender || e.senderEmail)}
          </div>

          {/* From / To / Date */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <div>
                <span style={{ fontWeight: 600, fontSize: '13.5px', color: 'var(--text)' }}>{e.sender}</span>
                {e.senderEmail && e.senderEmail !== e.sender && (
                  <span style={{ fontSize: '12px', color: 'var(--text-3)', marginLeft: '6px' }}>
                    &lt;{e.senderEmail}&gt;
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <span style={{ fontSize: '12px', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                  {new Date(e.receivedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </span>
                {/* Status badge */}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  background: sc.bg, color: sc.text,
                  borderRadius: '2px', padding: '2px 8px',
                  fontSize: '11px', fontWeight: 600,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: sc.dot, display: 'inline-block' }} />
                  {e.status}
                </span>
              </div>
            </div>
            <div style={{ marginTop: '4px', fontSize: '12px', color: 'var(--text-3)' }}>
              <span style={{ marginRight: '4px' }}>To:</span>
              <span style={{ color: 'var(--text-2)' }}>{e.recipients.join(', ') || e.mailboxEmail}</span>
              {e.ccRecipients.length > 0 && (
                <>
                  <span style={{ margin: '0 6px', color: 'var(--border-strong)' }}>·</span>
                  <span style={{ marginRight: '4px' }}>CC:</span>
                  <span style={{ color: 'var(--text-2)' }}>{e.ccRecipients.join(', ')}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Action toolbar ── */}
        <div className="command-bar" style={{ margin: '0 -24px', padding: '4px 16px', borderBottom: 'none', borderTop: '1px solid var(--border)' }}>
          <button
            className="btn-cmd"
            onClick={handleCreateTask}
            disabled={taskState === 'loading' || taskState === 'done'}
            title={taskState === 'error' ? taskError : 'Create AMS task from this extraction'}
            style={{
              color: taskState === 'done' ? 'var(--success)' : taskState === 'error' ? 'var(--danger)' : 'var(--accent)',
              fontWeight: 600,
              opacity: taskState === 'loading' ? 0.65 : 1,
            }}
          >
            {taskState === 'loading' ? (
              <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #C8C6C4', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            ) : (
              <IconTask />
            )}
            {taskState === 'done' ? 'Task Created' : taskState === 'error' ? 'Retry' : 'Create Task'}
          </button>

          {e.blobName && (
            <>
              <div className="command-bar-separator" />
              <button
                className="btn-cmd"
                onClick={handleDownloadEml}
                disabled={emlState === 'loading'}
                title={emlState === 'error' ? 'Download failed — click to retry' : 'Download .eml file'}
                style={{
                  color: emlState === 'error' ? 'var(--danger)' : undefined,
                  opacity: emlState === 'loading' ? 0.65 : 1,
                }}
              >
                {emlState === 'loading' ? (
                  <span style={{ display: 'inline-block', width: 12, height: 12, border: '2px solid #C8C6C4', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                ) : (
                  <IconDownload />
                )}
                {emlState === 'error' ? 'Retry' : 'Download .EML'}
              </button>
            </>
          )}

          {e.importance && e.importance !== 'normal' && (
            <>
              <div className="command-bar-separator" />
              <span style={{ fontSize: '12px', color: e.importance === 'high' ? 'var(--danger)' : 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '4px', padding: '0 4px' }}>
                {e.importance === 'high' ? '▲ High Priority' : '▼ Low Priority'}
              </span>
            </>
          )}

          {e.categories.length > 0 && (
            <>
              <div className="command-bar-separator" />
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {e.categories.map((c, i) => (
                  <span key={i} style={{ background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: '2px', padding: '1px 7px', fontSize: '11px', fontWeight: 600 }}>
                    {c}
                  </span>
                ))}
              </div>
            </>
          )}

          {/* Email Action Buttons */}
          <div className="command-bar-separator" />
          <button
            className="btn-cmd"
            onClick={() => handleCompose('reply')}
            title="Reply to this email"
            style={{ color: 'var(--accent)' }}
          >
            ↩ Reply
          </button>
          <button
            className="btn-cmd"
            onClick={() => handleCompose('reply-all')}
            title="Reply to all recipients"
            style={{ color: 'var(--accent)' }}
          >
            ↩ Reply All
          </button>
          <button
            className="btn-cmd"
            onClick={() => handleCompose('forward')}
            title="Forward this email"
            style={{ color: 'var(--accent)' }}
          >
            ↪ Forward
          </button>
          <div className="command-bar-separator" />
          <button
            className="btn-cmd"
            onClick={() => handleCompose('compose')}
            title="Compose new email"
            style={{ color: 'var(--accent)', fontWeight: 600 }}
          >
            ✉ Compose
          </button>
        </div>
      </div>

      {/* Attachments */}
      {realAttachments.length > 0 && (
        <div className="card" style={{ padding: '14px 20px', marginBottom: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
            <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-2)', letterSpacing: '.04em', textTransform: 'uppercase' }}>
              Attachments
            </span>
            <span style={{
              background: 'var(--accent-light)', color: 'var(--accent)',
              borderRadius: '10px', padding: '0 7px',
              fontSize: '11px', fontWeight: 700, lineHeight: '18px',
            }}>
              {realAttachments.length}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {realAttachments.map((att) => (
              <AttachmentCard key={att.id} att={att} extractionId={e.id} />
            ))}
          </div>
        </div>
      )}

      {/* Tasks */}
      {e.tickets && e.tickets.length > 0 && (
        <TasksPanel tickets={e.tickets} />
      )}

      {/* Body */}
      {(e.bodyHtml || e.bodyText) ? (
        <div>
          <SectionLabel>Email Body</SectionLabel>
          <BodyViewer bodyHtml={e.bodyHtml} bodyText={e.bodyText} />
        </div>
      ) : (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-3)', background: '#f8fafc', borderRadius: 'var(--radius)', border: '1px dashed var(--border)' }}>
          No email body content
        </div>
      )}

      {/* Error */}
      {e.error && (
        <div style={{ marginTop: '16px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: '13px', color: '#dc2626' }}>
          <strong>Error:</strong> {e.error}
        </div>
      )}

      <ComposeModal
        key={composeMode}
        isOpen={composeMode !== null}
        onClose={() => setComposeMode(null)}
        mode={composeMode as any}
        extractionId={e.id}
        extractionSubject={e.subject}
        toRecipients={composeMode === 'reply-all' || composeMode === 'reply' ? [{ emailAddress: e.senderEmail }] : []}
        ccRecipients={composeMode === 'reply-all' ? e.ccRecipients.map((cc) => ({ emailAddress: cc })) : []}
        senderEmail={e.mailboxEmail}
      />
    </div>
  );
};

export default EmailDetail;
